import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { api } from '../api';
import { formatWeekdayNames } from '@physioedge/shared';
import { BranchStaffManager, type BranchStaffMember } from '../components/BranchStaffManager';
import { useSupportPhone } from '../components/WhatsAppHelp';
import {
  Card, StatusBadge, Table, PageLoader, EmptyState,
} from '../components/ui';
import { FormattedText } from '../lib/simpleMarkup';

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-bg">
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        {children}
      </div>
    </div>
  );
}

function useClinicMe() {
  return useQuery({
    queryKey: ['clinic-me'],
    queryFn: () => api.getClinicMe(),
  });
}

// ─── Clinic details (name, contact, logo, about) ─────────────────────────────

export function ClinicProfilePage({ embedded = false }: { embedded?: boolean } = {}) {
  const clinicQ = useClinicMe();
  const clinic = clinicQ.data?.clinic;
  const settingsQ = useSupportPhone();
  const studentRules = clinic?.studentPolicyText?.trim() || settingsQ.data?.clinicPolicy || '';
  const partnerTerms = clinic?.partnerTermsText?.trim()
    || settingsQ.data?.clinicPartnershipPolicy
    || '';

  const body = (
    <>
      {clinicQ.isLoading ? <PageLoader variant="form" /> : (
        <div className="space-y-4 max-w-xl">
          <Card className="p-4 sm:p-5 space-y-4">
            <p className="text-sm text-ink-soft">
              Clinic details are managed by PhysioEDGE admin. You can view them here.
            </p>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-radius bg-teal-soft overflow-hidden flex items-center justify-center shrink-0">
                {clinic?.logoUrl ? (
                  <img src={clinic.logoUrl} alt={clinic.name} className="w-full h-full object-cover" />
                ) : (
                  <Building2 className="w-7 h-7 text-teal" />
                )}
              </div>
              <div>
                <div className="font-medium text-ink">{clinic?.name ?? '—'}</div>
                <div className="text-sm text-ink-soft">{clinic?.contactEmail ?? '—'}</div>
                <div className="text-sm text-ink-soft">{clinic?.contactPhone ?? '—'}</div>
              </div>
            </div>
            {clinic?.description && (
              <p className="text-sm text-ink whitespace-pre-wrap">{clinic.description}</p>
            )}
          </Card>
          {partnerTerms && (
            <Card className="p-4 sm:p-5 space-y-2">
              <div className="font-medium text-ink">Partner terms</div>
              <p className="text-xs text-ink-soft">Agreement with PhysioEDGE. Accepted by the main clinic account on first login.</p>
              <FormattedText text={partnerTerms} className="text-sm text-ink-soft" />
            </Card>
          )}
          {studentRules && (
            <Card className="p-4 sm:p-5 space-y-2">
              <div className="font-medium text-ink">Clinical rules for students</div>
              <p className="text-xs text-ink-soft">Students must accept these before they confirm a booking.</p>
              <FormattedText text={studentRules} className="text-sm text-ink-soft" />
            </Card>
          )}
        </div>
      )}
    </>
  );

  if (embedded) return body;
  return <Layout>{body}</Layout>;
}

// ─── Schedules ───────────────────────────────────────────────────────────────

export function ClinicSchedulesPage({ embedded = false }: { embedded?: boolean } = {}) {
  const schedulesQ = useQuery({
    queryKey: ['clinic-schedules'],
    queryFn: () => api.get<{ schedules: any[] }>('/api/clinic/schedules'),
  });

  const schedules = schedulesQ.data?.schedules ?? [];

  const body = (
    <>
      <p className="text-sm text-ink-soft mb-4">
        Set by PhysioEDGE admin. Hidden ones stay listed here but students cannot book them.
      </p>
      {schedulesQ.isLoading ? <PageLoader variant="table" /> : schedules.length === 0 ? (
        <EmptyState message="No schedules yet." />
      ) : (
        <>
          <div className="md:hidden space-y-3">
            {schedules.map((s: any) => (
              <Card key={s.id} className="p-4 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-ink">{s.title || 'Internship'}</div>
                  <StatusBadge status={s.isPublished ? 'published' : 'unpublished'} />
                </div>
                <div className="text-sm text-ink-soft">
                  {formatWeekdayNames(s.daysOfWeek ?? []) || 'No days'}
                  {' · '}
                  {s.dailyStartTime}–{s.dailyEndTime}
                </div>
                <div className="text-xs text-ink-faint">{s.startDate} → {s.endDate}</div>
              </Card>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <Table
              columns={[
                { key: 'title', header: 'Name', render: s => s.title || 'Internship' },
                {
                  key: 'when',
                  header: 'When',
                  render: s => (
                    <span className="text-ink-soft whitespace-normal">
                      {formatWeekdayNames(s.daysOfWeek ?? []) || 'No days'}
                      {' · '}
                      {s.dailyStartTime}–{s.dailyEndTime}
                    </span>
                  ),
                },
                { key: 'dates', header: 'Dates', render: s => `${s.startDate} → ${s.endDate}` },
                { key: 'status', header: 'Status', render: s => <StatusBadge status={s.isPublished ? 'published' : 'unpublished'} /> },
              ]}
              data={schedules}
              empty="No schedules."
            />
          </div>
        </>
      )}
    </>
  );

  if (embedded) return body;
  return <Layout>{body}</Layout>;
}

export function ClinicSetupPage() {
  const clinicQ = useClinicMe();
  const qc = useQueryClient();
  const isPrimary = clinicQ.data?.isPrimary === true;
  const staffQ = useQuery({
    queryKey: ['clinic-staff'],
    queryFn: () => api.get<{ staffMembers: BranchStaffMember[] }>('/api/clinic/staff'),
    enabled: isPrimary,
  });

  return (
    <Layout>
      <div className="mb-6">
        <Link to="/clinic" className="text-sm text-sand-strong font-medium hover:underline">
          ← Back
        </Link>
        <h1 className="font-heading text-2xl font-bold text-ink mt-2">Management</h1>
      </div>
      <div className="space-y-8 max-w-4xl">
        <section>
          <h2 className="font-heading text-lg font-bold text-ink mb-3">Clinic</h2>
          <ClinicProfilePage embedded />
        </section>
        {isPrimary && (
          <section>
            <h2 className="font-heading text-lg font-bold text-ink mb-1">Staff & branches</h2>
            <p className="text-sm text-ink-soft mb-3">
              Main account sees all branches. Assign which ones each staff login can manage.
            </p>
            {staffQ.isLoading || clinicQ.isLoading ? (
              <PageLoader variant="form" />
            ) : (
              <Card className="p-4 sm:p-5">
                <BranchStaffManager
                  locations={(clinicQ.data?.locations ?? []).map((l: { id: string; name: string }) => ({
                    id: l.id,
                    name: l.name,
                  }))}
                  staffMembers={staffQ.data?.staffMembers ?? []}
                  onCreate={async input => {
                    const res = await api.post<{
                      staff: { email: string };
                      password?: string;
                      temporaryPassword?: string;
                      credentialsEmailed: boolean;
                    }>('/api/clinic/staff', input);
                    await qc.invalidateQueries({ queryKey: ['clinic-staff'] });
                    return {
                      email: res.staff.email,
                      password: res.password ?? res.temporaryPassword,
                      temporaryPassword: res.temporaryPassword,
                      credentialsEmailed: res.credentialsEmailed,
                    };
                  }}
                  onAssignBranches={async (staffId, locationIds) => {
                    await api.patch(`/api/clinic/staff/${staffId}`, { locationIds });
                    await qc.invalidateQueries({ queryKey: ['clinic-staff'] });
                  }}
                />
              </Card>
            )}
          </section>
        )}
        <section>
          <h2 className="font-heading text-lg font-bold text-ink mb-3">Schedules</h2>
          <ClinicSchedulesPage embedded />
        </section>
      </div>
    </Layout>
  );
}

/** @deprecated Prefer specific Clinic*Page exports with routes. */
/** @deprecated Prefer ClinicOverviewPage. */
export function ClinicDashboardPage() {
  return <ClinicSetupPage />;
}
